import ArticleIcon from '@mui/icons-material/Article'
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined'
import NorthEastIcon from '@mui/icons-material/NorthEast'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import {
  Box,
  CardHeader,
  Chip,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import type { Theme } from '@mui/material/styles'
import type { ReactElement, ReactNode } from 'react'
import { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import CopyableId from '@/components/dataDisplay/CopyableId'
import CustomTypography from '@/components/dataDisplay/CustomTypography'
import { ControllerRouteInformation } from '@/features/controller/constants'
import { makeControllerLogSearchUrl } from '@/features/controller/utils/coralogixLinks'
import { makeGrafanaLinkForControllerEquipmentNumber } from '@/features/controller/utils/grafanaLinks'
import PortTypeChip from '@/features/resource/components/PortTypeChip'
import ResourcePortsGraph from '@/features/resource/components/ResourcePortsGraph'
import { portRowElementId } from '@/features/resource/hooks/usePortHighlight'
import type { Resource } from '@/features/resource/types'
import { getControlBoxTypeLabel } from '@/features/resource/utils/controlBoxTypes'
import { targetControllerLabel } from '@/features/resource/utils/controllerLabel'
import { deviceErrorsAreLive } from '@/features/resource/utils/deviceErrorLiveness'
import { portCopyIdLabel } from '@/features/resource/utils/portCopyLabel'
import type { ControllerGroup, GroupedPort, PortReference } from '@/features/resource/utils/portGroups'
import { buildControllerGroups, getPortReferences } from '@/features/resource/utils/portGroups'
import { truncateId } from '@/utils/truncateId'

/**
 * The same observability links the controller details page offers, in the same order and reading the
 * same `controllers.observability.*` strings — two sets for one vocabulary is how they drift apart.
 */
const CONTROLLER_LOG_RANGES = [
  {
    key: 'logs_15m',
    duration: 'last-15-minutes',
    filters: [] as string[],
    ariaKey: 'controller_group_logs_15m_aria_label',
    Icon: ArticleIcon,
  },
  {
    key: 'logs_2d',
    duration: 'last-2-days',
    filters: [] as string[],
    ariaKey: 'controller_group_logs_2d_aria_label',
    Icon: ArticleIcon,
  },
  {
    key: 'logs_errors_1d',
    duration: 'last-24-hours',
    filters: ['$m.severity == ERROR'],
    ariaKey: 'controller_group_logs_errors_1d_aria_label',
    Icon: ErrorOutlinedIcon,
  },
] as const

/** The narrowest `detail` that still reads; the column's own comment carries the measurements. */
const DETAIL_FLOOR = 180

/**
 * Declared once and rendered into every group's table, which is what makes the alignment structural:
 * independently sized tables put the same field at different x-positions however they are styled.
 *
 * One number per column, its floor, rather than a width beside it — the two would have to agree, and
 * the table's minimum is their sum. Fixed columns render at their floor; `detail` renders `auto`.
 */
const PORT_COLUMNS = [
  // 148px measured, not calculated: "Analog Output" needs exactly 132px of cell width including
  // padding, so at 132 it sat on the boundary and wrapped. The slack absorbs a longer translation.
  { key: 'type', floor: 148, headerKey: 'resources.detail_panel.type_header' },
  // 156px leaves 124px of content after padding, of which the copy control and its gap take 38px,
  // so the name has 86px against a longest fixture name of 78.7px. The margin is thin: re-measure
  // rather than estimating if the control changes size.
  { key: 'name', floor: 156, headerKey: 'resources.detail_panel.name_header' },
  { key: 'relatesTo', floor: 158, headerKey: 'resources.detail_panel.relates_to_header' },
  /*
   * The one flexible column, and the only floor that is a judgement rather than a measured string.
   * Without one, narrowing the window took every pixel from here first, painting Settings over
   * Detail's text until the right-hand columns left the viewport with no scrollbar.
   *
   * 180px, measured against every string this cell renders. The longest, "Control box: Huawei Smart
   * Logger", needs 219.6px on one line and takes two lines from 180px down to 161px, three below
   * that — so 180 is where the longest real value stops needing a third line. The hard floor where
   * glyphs overflow is 82px, and a no-wrap column would cost 252px.
   */
  { key: 'detail', flexible: true, floor: DETAIL_FLOOR, headerKey: 'resources.detail_panel.detail_header' },
  /*
   * 169px, measured by rendering. The caption sits above the value, so the column holds the wider of
   * the two rather than both side by side, which is what makes stacked cheaper horizontally.
   *
   *   Measure · Temperature   136.3px  <- widest
   *   Relation · Subtractive  127.7px
   *   Measure · Unknown       115.4px
   *   Relation · Additive     107.0px
   *
   * Plus 16px of padding each side. Confirmed by sweeping widths rather than by adding the numbers
   * up: sized from an estimate twice and wrong twice, the second time shipping a column that
   * rendered some cells inline and some stacked. Re-measure if a value joins either enum. Inline
   * would need 231px, and the 62px difference goes to `detail`.
   */
  { key: 'settings', floor: 169, headerKey: 'resources.detail_panel.settings_header' },
  { key: 'errors', floor: 104, headerKey: 'resources.detail_panel.errors_header' },
] as const

/**
 * The columns this resource's table actually has. The error column goes for a LOCAL resource rather
 * than filling with "N/A", which holds 104px and reads as easily as "no errors" as "not applicable".
 * `control_mode` belongs to the resource, so this stays a whole-table decision rather than per-row.
 */
function portColumnsFor(hasLiveDeviceErrors: boolean) {
  return hasLiveDeviceErrors ? PORT_COLUMNS : PORT_COLUMNS.filter((column) => column.key !== 'errors')
}

/** What a column is given in the colgroup: its floor, or `auto` for the one that flexes. */
function columnWidth(column: (typeof PORT_COLUMNS)[number]): string {
  return 'flexible' in column && column.flexible ? 'auto' : `${column.floor}px`
}

/**
 * The width below which the table scrolls instead of shrinking. Summed from the floors so it cannot
 * drift from them, and it differs by control mode, since a LOCAL resource has no errors column.
 */
function tableMinWidth(columns: readonly (typeof PORT_COLUMNS)[number][]): number {
  return columns.reduce((total, column) => total + column.floor, 0)
}

/**
 * Above this many relations the row stops being a line and becomes a block. Cells are centred by
 * default, which is right for one line and wrong for a row of fifteen relations standing 612px: the
 * type, name, detail and settings floated hundreds of pixels below what they belong to.
 */
const RELATIONS_BEFORE_TOP_ALIGNING = 3

const highlightableRowSx = (theme: Theme) => ({
  scrollMarginBlock: theme.spacing(3),
  transition: 'background-color 200ms ease-out',
  '&[data-highlighted="true"]': {
    backgroundColor: 'action.selected',
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: '-2px',
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
})

function PortReferenceLink({
  reference,
  onShowPort,
}: Readonly<{ reference: PortReference; onShowPort: (portId: string) => void }>) {
  const { t } = useTranslation()

  // Truncation is a visual measure only. The spoken name keeps the id whole: a nameless port would
  // otherwise announce as an eight-character fragment ending in an ellipsis, with no tooltip to
  // recover the rest — and since the legend went, this label is the only description of the
  // relation assistive technology gets.
  const label = reference.targetPortName ?? truncateId(reference.targetPortId)
  const spokenName = reference.targetPortName ?? reference.targetPortId
  const accessibleName = reference.isCrossController
    ? t('resources.detail_panel.show_related_port_on_other_controller', {
        portName: spokenName,
        controllerName: targetControllerLabel(reference, t),
      })
    : t('resources.detail_panel.show_related_port', { portName: spokenName })

  const { targetControllerEquipmentNumber } = reference

  /*
   * Nothing to jump to, so nothing that looks like a jump. The relation is still reported: a control
   * port naming a metering port this resource does not list is wired to something, which is a
   * different fact from being wired to nothing, and the id is the only part of it we have.
   */
  if (!reference.targetIsListed) {
    return (
      <CustomTypography color="grey.500" variant="inherit">
        {t('resources.detail_panel.measured_by_unlisted_port', { portId: truncateId(reference.targetPortId) })}
      </CustomTypography>
    )
  }

  return (
    <Stack alignItems="flex-start">
      {/*
       * The port name, and only the port name, is the jump. The controller identifier stays outside
       * this button: inside it, it would look like a controller link while behaving like a row jump.
       * It is a link in its own right below, doing what it looks like.
       */}
      {/*
       * Breaks anywhere as a last resort, for the same reason the Name cell does: a related port
       * whose name is one unbreakable word ran out of this cell and across Detail.
       */}
      <MuiLink
        aria-label={accessibleName}
        component="button"
        sx={{ textAlign: 'left', minWidth: 0, overflowWrap: 'anywhere' }}
        type="button"
        onClick={() => onShowPort(reference.targetPortId)}
      >
        {label}
        {/*
         * The arrow is the only marker of a relation leaving its controller, and it belongs to this
         * name rather than to the cell. Outside the button, in a flex row, it would sit after the
         * button's whole box: once a long name wraps, the button fills the column and the arrow is
         * pushed to the far right edge at the first line's height, floating between two references
         * and attached to neither.
         *
         * Inline inside the label it flows with the text, so it follows the last word onto whatever
         * line that word lands on. `text-bottom` sits it on that line's baseline box rather than
         * on the button's.
         */}
        {reference.isCrossController && (
          <NorthEastIcon fontSize="inherit" sx={{ verticalAlign: 'text-bottom', marginLeft: 0.25 }} />
        )}
      </MuiLink>

      {/*
       * Always the equipment number, never the controller's name. Alternating by which the target
       * has is defensible and reads as a bug: one row saying "Equipment no. 910003" beside another
       * saying "Wolf 910002" looks like inconsistency rather than like two kinds of identifier. The
       * number is also what matches the group headers, which is what the reader is scanning for.
       *
       * Nothing is rendered when the target controller has no equipment number, because there is no
       * identifier to show and no page to link to. The relation is not hidden by that: the arrow
       * still marks the crossing, the jump's accessible name still says the destination controller
       * is unidentified, and a same-controller reference of that kind sits under the unidentified
       * group's own header, which says so directly. What it does cost is a sighted reader on a
       * crossing row seeing an arrow with no destination beside it — accepted, because the
       * alternative is a second visual shape in the cell saying only "we still do not know".
       */}
      {targetControllerEquipmentNumber !== null && (
        <MuiLink
          aria-label={t('resources.detail_panel.open_target_controller', {
            equipmentNumber: targetControllerEquipmentNumber,
          })}
          component={Link}
          to={ControllerRouteInformation.CONTROLLER_BY_EQUIPMENT_NUMBER.navigationPath(targetControllerEquipmentNumber)}
          variant="caption"
        >
          {targetControllerEquipmentNumber}
        </MuiLink>
      )}
    </Stack>
  )
}

/**
 * One observability link. Renders nothing without a URL, so a group with no equipment number offers
 * no dead links rather than four that filter to nothing.
 */
function ObservabilityChip({
  icon,
  label,
  accessibleName,
  url,
}: Readonly<{ icon: ReactElement; label: string; accessibleName: string; url: string | null }>) {
  // Unreachable today — the only caller renders these inside the `equipmentNumber !== null` arm —
  // but it gives the null a builder can return a defined rendering at the point of use.
  if (url === null) {
    return null
  }

  return (
    <MuiLink aria-label={accessibleName} href={url} rel="noreferrer" target="_blank">
      <Chip color="default" icon={icon} label={label} size="small" variant="outlined" />
    </MuiLink>
  )
}

function ControllerGroupObservabilityLinks({ group, siteLocation }: Readonly<GroupObservabilityProps>) {
  const { t } = useTranslation()

  const grafanaUrl = makeGrafanaLinkForControllerEquipmentNumber(group.equipmentNumber, siteLocation)

  return (
    <>
      {/*
       * Captions carried over from the controller page rather than dropped for space: the range
       * labels never say "logs", so "Last 15 min" would have to be inferred, and "Metrics" is what
       * separates Grafana from the three beside it.
       */}
      <Stack alignItems="baseline" direction="row" gap={0.75}>
        <CustomTypography color="grey.500" variant="caption">
          {t('controllers.observability.metrics')}
        </CustomTypography>
        <ObservabilityChip
          accessibleName={t('resources.detail_panel.controller_group_grafana_aria_label', {
            equipmentNumber: group.equipmentNumber,
          })}
          icon={<TrendingUpIcon />}
          label={t('controllers.observability.grafana')}
          url={grafanaUrl}
        />
      </Stack>
      <Stack alignItems="baseline" direction="row" flexWrap="wrap" gap={0.75}>
        <CustomTypography color="grey.500" variant="caption">
          {t('controllers.observability.logs')}
        </CustomTypography>
        {CONTROLLER_LOG_RANGES.map((range) => (
          <ObservabilityChip
            key={range.key}
            accessibleName={t(`resources.detail_panel.${range.ariaKey}`, {
              equipmentNumber: group.equipmentNumber,
            })}
            icon={<range.Icon />}
            label={t(`controllers.observability.${range.key}`)}
            url={makeControllerLogSearchUrl(group.equipmentNumber, range.duration, [...range.filters])}
          />
        ))}
      </Stack>
    </>
  )
}

type GroupObservabilityProps = {
  group: ControllerGroup
  /** The resource's site location, which is all a resource can offer Grafana's `var-location`. */
  siteLocation: string | null
}

function ControllerGroupHeader({ group, siteLocation }: Readonly<GroupObservabilityProps>) {
  const { t } = useTranslation()

  // The same phrase the relates-to column uses for the same destination: both open the controller
  // page, so naming one of them after the number and the other after the action read as two links.
  const equipmentNumberLabel = t('resources.detail_panel.open_target_controller', {
    equipmentNumber: group.equipmentNumber,
  })

  return (
    <Stack
      alignItems="baseline"
      direction="row"
      flexWrap="wrap"
      gap={1.5}
      sx={{
        backgroundColor: group.equipmentNumber === null ? 'transparent' : 'action.hover',
        borderBottom: 1,
        borderColor: 'divider',
        paddingX: 2,
        paddingY: 1,
      }}
    >
      <CustomTypography color="grey.500" variant="caption">
        {t('resources.detail_panel.controller_group_label')}
      </CustomTypography>

      {group.equipmentNumber === null ? (
        <>
          <CustomTypography color="grey.500" variant="inherit">
            {t('resources.detail_panel.controller_group_unknown')}
          </CustomTypography>
          <CustomTypography color="grey.500" variant="caption">
            {t('resources.detail_panel.controller_group_unknown_note')}
          </CustomTypography>
          <Box flexGrow={1} />
          <CustomTypography color="grey.500" variant="caption">
            {t('resources.detail_panel.controller_group_no_observability_links')}
          </CustomTypography>
        </>
      ) : (
        <>
          {group.controllerName === null ? (
            /*
             * Said out loud rather than left as a blank where the name would be.
             *
             * A controller contributing only control ports to a resource has an equipment number and
             * no name — guaranteed by the model, since a controller name reaches the API only
             * through a metering port. Rendering nothing there makes a guaranteed state look like a
             * value that failed to load. The graph already names it; this is the same phrase.
             */
            <CustomTypography color="grey.500" fontStyle="italic" variant="inherit">
              {t('resources.detail_panel.controller_group_no_name')}
            </CustomTypography>
          ) : (
            <CustomTypography fontWeight={600} variant="inherit">
              {group.controllerName}
            </CustomTypography>
          )}
          <CustomTypography color="grey.500" variant="caption">
            {/* Whole phrase, so a translator can move the number within it rather than being handed
                a bare "Equipment no." to place beside a value they cannot reach. */}
            <Trans
              components={{
                equipmentNumberLink: (
                  <MuiLink
                    aria-label={equipmentNumberLabel}
                    component={Link}
                    to={ControllerRouteInformation.CONTROLLER_BY_EQUIPMENT_NUMBER.navigationPath(group.equipmentNumber)}
                  />
                ),
              }}
              i18nKey="resources.detail_panel.controller_group_equipment_number"
              values={{ equipmentNumber: group.equipmentNumber }}
            />
          </CustomTypography>
          {group.controllerImei !== null && (
            <CustomTypography color="grey.500" variant="caption">
              {t('resources.detail_panel.controller_group_imei', { imei: group.controllerImei })}
            </CustomTypography>
          )}
          <Box flexGrow={1} />
          <ControllerGroupObservabilityLinks group={group} siteLocation={siteLocation} />
        </>
      )}
    </Stack>
  )
}

type PortsView = 'TABLE' | 'GRAPH'

/**
 * Table or graph, offered regardless of port count. A graph scales down worse than up — two nodes
 * say less than two rows — but hiding the switch would make the section's shape depend on data the
 * operator cannot see, and leave the view that answers "does this relation cross a controller"
 * available only where the resource happens to be busy.
 */
function PortsViewSwitch({ value, onChange }: Readonly<{ value: PortsView; onChange: (view: PortsView) => void }>) {
  const { t } = useTranslation()

  return (
    <ToggleButtonGroup
      exclusive
      aria-label={t('resources.detail_panel.ports_view_switch_label')}
      color="primary"
      size="small"
      sx={{ '& .MuiToggleButton-root': { textTransform: 'none', paddingY: 0.25, paddingX: 1.5 } }}
      value={value}
      // MUI reports null when the pressed button is the active one. Keeping the current view is
      // right here: there is no "neither" state to fall back to.
      onChange={(_event, next: PortsView | null) => next !== null && onChange(next)}
    >
      <ToggleButton value="TABLE">{t('resources.detail_panel.ports_view_table')}</ToggleButton>
      <ToggleButton value="GRAPH">{t('resources.detail_panel.ports_view_graph')}</ToggleButton>
    </ToggleButtonGroup>
  )
}

export type ResourcePortsSectionProps = {
  resource: Resource
  highlightedPortId: string | null
  onShowPort: (portId: string) => void
  /** The row's editable value, which only some port kinds have. Supplied by the card, which owns the mutations. */
  renderSettings: (port: GroupedPort) => ReactNode
}

/**
 * The port's name, and the only place its id is still reachable.
 *
 * The ID column this replaces was a whole column spent on a value nobody reads — but support staff
 * quote these ids, so removing the column had to keep them copyable rather than merely visible.
 *
 * The name stays plain text and the id gets a copy button beside it. Making the name itself the
 * button, as this first did, cost more than it bought: text inside a button cannot be drag-selected,
 * so the name — the thing a TSE actually quotes in a ticket — became impossible to copy at all, in
 * exchange for a larger target on an id that is quoted far less often.
 *
 * A port with no name shows the truncated id in the name's place — exactly what the ID column
 * showed. That row is otherwise unidentifiable, and an unidentifiable row is worse than a wide one.
 */
function PortNameCell({ port }: Readonly<{ port: GroupedPort }>) {
  const { t } = useTranslation()

  return <CopyableId copyLabel={portCopyIdLabel(port, t)} displayText={port.name ?? undefined} value={port.id} />
}

function PortDetailCell({ port }: Readonly<{ port: GroupedPort }>) {
  const { t } = useTranslation()

  if (port.kind === 'METERING') {
    return (
      <>
        {port.meteringPort.isSingleResource
          ? t('resources.detail_panel.single_resource')
          : t('resources.detail_panel.multiple_resources')}
      </>
    )
  }

  // The signal type moved to the Type column, so naming what remains keeps a bare "Battery" from
  // sitting here unexplained.
  return (
    <>
      {t('resources.detail_panel.port_detail_control_box', {
        controlBoxType: getControlBoxTypeLabel(port.controlPort.controlBoxType, t),
      })}
    </>
  )
}

function ControllerGroupTable({
  group,
  referencesByPortId,
  highlightedPortId,
  onShowPort,
  renderSettings,
  hasLiveDeviceErrors,
}: Readonly<{
  group: ControllerGroup
  referencesByPortId: ReadonlyMap<string, PortReference[]>
  highlightedPortId: string | null
  onShowPort: (portId: string) => void
  renderSettings: ResourcePortsSectionProps['renderSettings']
  hasLiveDeviceErrors: boolean
}>) {
  const { t } = useTranslation()

  const columns = portColumnsFor(hasLiveDeviceErrors)

  // Three tables all named "Ports" left a screen-reader user unable to tell which controller they
  // had landed in — something the three named tables this replaced did convey.
  const tableLabel = (() => {
    if (group.controllerName !== null) {
      return t('resources.detail_panel.ports_table_label', { controllerName: group.controllerName })
    }
    if (group.equipmentNumber !== null) {
      return t('resources.detail_panel.ports_table_label_by_equipment_number', {
        equipmentNumber: group.equipmentNumber,
      })
    }

    return t('resources.detail_panel.ports_table_label_unknown')
  })()

  return (
    /*
     * The table scrolls sideways here; the page never does.
     *
     * Below the sum of the columns' floors this wrapper scrolls rather than the table shrinking
     * further, which is what keeps the right-hand columns reachable. They were not reachable before:
     * the group Box outside this one clips with `overflow: hidden`, so once the table outgrew it the
     * columns were simply cut off, with no scrollbar anywhere to get them back.
     *
     * A new wrapper rather than relaxing that Box. Its `overflow: hidden` is load-bearing for the
     * group's rounded corners, and `overflow-x: auto` on it would coerce its `overflow-y` from
     * `visible` to `auto` and put a vertical scrollbar back on the group — the same coercion behind
     * the double scrollbar the graph stage had for a pixel of fractional-height rounding. The axes
     * are not independent.
     *
     * Which is why `overflowY` is spelled out here as well: asking for horizontal scrolling silently
     * asks for vertical scrolling too, and since the table has no height constraint to overflow, the
     * only vertical scrollbar this could produce is one for a pixel of rounding.
     *
     * Nothing is needed to let this shrink. It carried a `minWidth: 0` and a comment claiming it was
     * a flex item that would otherwise refuse to narrow; it is a plain block inside the group Box,
     * so its minimum is already zero and the property did nothing. The group Box *is* the flex item,
     * and in a column Stack width is the cross axis, where `min-width: auto` is not in play either.
     */
    <Box sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
      <Table
        aria-label={tableLabel}
        size="small"
        sx={{ tableLayout: 'fixed', minWidth: `${tableMinWidth(columns)}px` }}
      >
        <colgroup>
          {columns.map((column) => (
            <col key={column.key} style={{ width: columnWidth(column) }} />
          ))}
        </colgroup>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.key}>{t(column.headerKey)}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {group.ports.map((port) => {
            const references = referencesByPortId.get(port.id) ?? []
            // On each cell rather than on the row, since vertical-align applies to the cell box.
            const cellSx =
              references.length > RELATIONS_BEFORE_TOP_ALIGNING ? { verticalAlign: 'top' as const } : undefined

            return (
              <TableRow
                key={port.id}
                data-highlighted={highlightedPortId === port.id}
                data-port-row=""
                id={portRowElementId(port.id)}
                sx={highlightableRowSx}
                tabIndex={-1}
              >
                <TableCell sx={cellSx}>
                  <PortTypeChip port={port} />
                </TableCell>
                <TableCell sx={cellSx}>
                  <PortNameCell port={port} />
                </TableCell>
                <TableCell sx={cellSx}>
                  {references.length === 0 ? (
                    <CustomTypography color="grey.500" variant="inherit">
                      -
                    </CustomTypography>
                  ) : (
                    references.map((reference) => (
                      <PortReferenceLink key={reference.targetPortId} reference={reference} onShowPort={onShowPort} />
                    ))
                  )}
                </TableCell>
                <TableCell sx={cellSx}>
                  <PortDetailCell port={port} />
                </TableCell>
                <TableCell sx={cellSx}>{renderSettings(port)}</TableCell>
                {hasLiveDeviceErrors && (
                  <TableCell sx={{ ...cellSx, whiteSpace: 'pre-wrap' }}>{port.errors ?? '-'}</TableCell>
                )}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Box>
  )
}

/**
 * Every port on the resource, grouped by the controller it sits on. Splitting by kind would answer
 * "what kind of port is this", which the Type column answers in a cell; grouping answers the
 * question the section is named after.
 */
export default function ResourcePortsSection({
  resource,
  highlightedPortId,
  onShowPort,
  renderSettings,
}: Readonly<ResourcePortsSectionProps>) {
  const { t } = useTranslation()

  // The table stays the default: it is where identifiers are copied and settings changed, and it
  // stays scannable at both ends of the range, where a two-node graph says less than two rows.
  const [view, setView] = useState<PortsView>('TABLE')

  // Memoised because `buildControllerGroups` returns fresh objects every call: without this the
  // graph and the references map get new inputs on every render and their own `useMemo`s never hit,
  // so the work is redone whatever the port counts happen to be.
  const groups = useMemo(() => buildControllerGroups(resource), [resource])

  // Computed once for the whole section rather than per row on every render, and shared with the
  // graph so switching views resolves no relation a second time.
  const referencesByPortId = useMemo(
    () =>
      new Map(
        groups.flatMap((group) => group.ports.map((port) => [port.id, getPortReferences(port, resource)] as const)),
      ),
    [groups, resource],
  )

  if (groups.length === 0) {
    return null
  }

  const hasLiveDeviceErrors = deviceErrorsAreLive(resource)

  return (
    <>
      <Stack alignItems="baseline" direction="row" flexWrap="wrap" gap={2} justifyContent="space-between">
        <CardHeader sx={{ paddingX: 0 }} title={t('resources.detail_panel.controllers_section_header')}></CardHeader>
        <PortsViewSwitch value={view} onChange={setView} />
      </Stack>

      {/*
       * Once, at section level. `control_mode` is a property of the resource, so this applies to
       * every row here — a note per row or per group would repeat one fact as many times as there
       * are ports. Without it a missing column reads as a bug rather than as a decision.
       *
       * **What this sentence can truthfully say depends on what the page shows**, which is why it
       * has now been written both ways and why the key is named for its subject rather than for its
       * claim.
       *
       * While the field panel still listed a frozen `errors` value, a blanket "not reported" was
       * refuted a few centimetres below by a field with a value in it, so the note had to talk about
       * staleness instead — and had to, because the dangerous case was never the frozen
       * `NOT_RESPONDING` that looks like data but the frozen `None` that reads as an all-clear.
       *
       * Now that the panel omits the row too, no error value reaches the screen for a LOCAL
       * resource. "Any value shown" would describe something the reader cannot find, and the plain
       * claim is true again. Change what the page displays and check this sentence still holds.
       */}
      {!hasLiveDeviceErrors && (
        <CustomTypography color="grey.500" variant="caption">
          {t('resources.detail_panel.local_control_device_errors_note')}
        </CustomTypography>
      )}

      {view === 'TABLE' ? (
        <Stack gap={1.5} paddingTop={1}>
          {groups.map((group) => (
            <Box
              key={group.equipmentNumber ?? 'unknown'}
              data-controller-group=""
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}
            >
              <ControllerGroupHeader group={group} siteLocation={resource.siteLocation} />
              <ControllerGroupTable
                group={group}
                hasLiveDeviceErrors={hasLiveDeviceErrors}
                highlightedPortId={highlightedPortId}
                referencesByPortId={referencesByPortId}
                renderSettings={renderSettings}
                onShowPort={onShowPort}
              />
            </Box>
          ))}
        </Stack>
      ) : (
        <Box
          data-ports-graph=""
          marginTop={1}
          sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}
        >
          <ResourcePortsGraph
            groups={groups}
            hasLiveDeviceErrors={hasLiveDeviceErrors}
            referencesByPortId={referencesByPortId}
          />
        </Box>
      )}
    </>
  )
}
